import React, { useState, useEffect, useRef, useCallback } from "react";
import Draggable from "react-draggable";
import "../../App.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAnglesDown,
  faAnglesRight,
  faClose,
  faCompress,
  faExpand,
  faPlus,
} from "@fortawesome/free-solid-svg-icons";
import { LocationMarkerPopup } from "../../mainLayer/MainWindow";
import clock from "../../assets/clock.png";
import { ResizableBox, ResizeCallbackData } from "react-resizable";
import { map } from "../MainLayer";
import { Coordinate } from "ol/coordinate";
import Overlay from "ol/Overlay";
import { openWindowsRainfallMap, rainfallMap } from "./Rainfall";
import RainLastDayChart from "./RainLastDayChart";
import RainCurrentChart from "./RainCurrentChart";
import RainDiffChart from "./RainDiffChart";

interface Props {
  sensorId: string;
  name: string;
  // cautionLevel: number;
  // warningLevel: number;
  // criticalLevel: number;
  onClose: (incidentId: string) => void;
  addItem?: (id: string, type: "ew" | "river" | "air" | "hz" | "rain") => void;
  id?: string;
}

const RainfallPopup: React.FC<Props> = (props) => {
  const [showDialog, setShowDialog] = useState<boolean>(true);
  const [lastUpdate, setLastUpdate] = useState<string | null>(null);
  const [showSensorDetails, setShowSensorDetails] = useState(false);
  const [activeFullScreen, setActiveFullScreen] = useState<boolean>(false);
  const fullscreenModal = useRef<HTMLDivElement | null>(null);
  const parentContainerRef = useRef<HTMLDivElement | null>(null);
  const [showDescription, setShowDescription] = useState(false);
  const [widthMain, setWidthMain] = useState<number>(0);
  const [heightMain, setHeightMain] = useState<number>(0);
  const [isDark, setIsDark] = useState<boolean>(false);
  const [current, setCurrent] = useState<string>("");
  const [last24, setLast24] = useState<string>("");
  const [diff, setDiff] = useState<string>("");

  const [dimensions, setDimensions] = useState({
    resizableBoxwidth: 0,
    resizableBoxheight: 0,
  });

  const [dimensionsMin, setDimensionsMin] = useState({
    resizableBoxMinwidth: 0,
    resizableBoxMinheight: 0,
  });

  //----------------resibleBox Function----------------

  const updateDimensions = () => {
    const vw = Math.max(
      document.documentElement.clientWidth || 0,
      window.innerWidth || 0
    );
    const vh = Math.max(
      document.documentElement.clientHeight || 0,
      window.innerHeight || 0
    );

    // setDimensions({
    //   resizableBoxwidth: vw * 0.68,
    //   resizableBoxheight: vh * 0.75,
    // });

    // setDimensionsMin({
    //   resizableBoxMinwidth: vw * 0.4,
    //   resizableBoxMinheight: vh * 0.72,
    // });

    if (vw > 2000 && vh > 1200) {
      setDimensions({
        resizableBoxwidth: vw * 0.4,
        resizableBoxheight: vh * 0.5,
      });
    } else {
      setDimensions({
        resizableBoxwidth: vw * 0.3,
        resizableBoxheight: vh * 0.6,
      });
    }

    if (vw > 2000 && vh > 1200) {
      setDimensionsMin({
        resizableBoxMinwidth: vw * 0.2,
        resizableBoxMinheight: vh * 0.3,
      });
    } else {
      setDimensionsMin({
        resizableBoxMinwidth: vw * 0.3,
        resizableBoxMinheight: vh * 0.4,
      });
    }
  };

  useEffect(() => {
    updateDimensions();
    window.addEventListener("resize", updateDimensions);
    return () => window.removeEventListener("resize", updateDimensions);
  }, []);

  const openContentFullscreen = () => {
    if (!document.fullscreenElement) {
      enterFullscreen();
      setShowSensorDetails(true);
    } else {
      exitFullScreen();
      setShowSensorDetails(false);
    }
  };

  const enterFullscreen = () => {
    const elem = fullscreenModal.current;
    if (elem) {
      if (elem.requestFullscreen) {
        elem.requestFullscreen();
      }
      setActiveFullScreen(true);
    }
  };

  const exitFullScreen = () => {
    if (document.exitFullscreen) {
      document.exitFullscreen();
    } else if ((document as any).mozCancelFullScreen) {
      (document as any).mozCancelFullScreen();
    } else if ((document as any).webkitExitFullscreen) {
      (document as any).webkitExitFullscreen();
    } else if ((document as any).msExitFullscreen) {
      (document as any).msExitFullscreen();
    }
    setActiveFullScreen(false);
  };

  const RAIN_DATA_URL = process.env.REACT_APP_RAIN_DATA_URL;
  const RAIN_24H_URL = process.env.REACT_APP_RAIN_24H_URL;
  const RAIN_DIFF_URL = process.env.REACT_APP_RAIN_DIFF_URL;

  useEffect(() => {
    const fetchCurrentSensorsData = async () => {
      try {
        const response = await fetch(
          `${RAIN_DATA_URL}` +
            props.sensorId
        );
        const responseData = await response.json();

        if (responseData && typeof responseData === "object") {
          const { time, level } = responseData;
          setLastUpdate(time);
          setCurrent(level);
        } else {
          console.error("Invalid response data format:", responseData);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    const fetchlast24SensorsData = async () => {
      try {
        const response = await fetch(
          `${RAIN_24H_URL}` +
            props.sensorId
        );
        const responseData = await response.json();

        if (responseData && typeof responseData === "object") {
          const { level } = responseData;
          setLast24(level);
        } else {
          console.error("Invalid response data format:", responseData);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    const fetchDiffData = async () => {
      try {
        const response = await fetch(
          `${RAIN_DIFF_URL}` +
            props.sensorId
        );
        const responseData = await response.json();

        if (responseData && typeof responseData === "object") {
          const { level } = responseData;
          setDiff(level);
        } else {
          console.error("Invalid response data format:", responseData);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchCurrentSensorsData();
    fetchlast24SensorsData();
    fetchDiffData();
    return () => {};
  }, [props.sensorId]);

  const date = new Date().toLocaleDateString();
  const time = new Date().toLocaleTimeString();

  const hideDialog = (sensorId: string) => {
    setShowDialog(false);
    LocationMarkerPopup("baseMap");
    if (rainfallMap.has(sensorId)) {
      rainfallMap.delete(sensorId);
    }
    props.onClose(sensorId);
    removeOverlay(sensorId);
    if (rainfallMap.has(sensorId)) {
      rainfallMap.delete(sensorId);
      openWindowsRainfallMap.set(sensorId, "close");
    }
    const entries = Array.from(rainfallMap.values());
    const lastEntryIndex = entries.length - 1;
    entries.forEach((details, index) => {
      const { coordinates, sensorId } = details;
      if (coordinates) {
        removeOverlay(sensorId!);
        if (index === lastEntryIndex) {
          addOverlayClickItemLast(sensorId!, coordinates);
        } else {
          addOverlayClickItem(sensorId!, coordinates);
        }
      }
    });
  };

  const removeOverlay = (key: string) => {
    const overlays = map.getOverlays().getArray();
    const overlaysToRemove = overlays.filter(
      (overlay) => overlay.getId() === key
    );
    overlaysToRemove.forEach((overlay) => {
      map.removeOverlay(overlay);
    });
  };

  const addOverlayClickItem = (id: string, coordinates: Coordinate) => {
    const markerElement = document.createElement("div");
    markerElement.className = "location-dot animate";

    const ringElement = document.createElement("div");
    ringElement.className = "location-dot-ring";

    markerElement.appendChild(ringElement);
    const overlay = new Overlay({
      id: id,
      position: coordinates,
      positioning: "center-center",
      element: markerElement,
      stopEvent: false,
    });
    map.addOverlay(overlay);
  };

  const addOverlayClickItemLast = (id: string, coordinates: Coordinate) => {
    const markerElement = document.createElement("div");
    markerElement.className = "locationlast-dot animate";
    const ringElement = document.createElement("div");
    ringElement.className = "locationlast-dot-ring";

    markerElement.appendChild(ringElement);
    const overlay = new Overlay({
      id: id,
      position: coordinates,
      positioning: "center-center",
      element: markerElement,
      stopEvent: false,
    });
    map.addOverlay(overlay);
  };

  useEffect(() => {}, [showDescription]);

  useEffect(() => {}, [widthMain, heightMain]);

  const handleResize = (
    event: React.SyntheticEvent,
    data: ResizeCallbackData
  ) => {
    setWidthMain(data.size.width);
    setHeightMain(data.size.height);

    if (data.size.width < 900) {
      // Set your desired width threshold
      setShowDescription(true);
    } else {
      setShowDescription(false);
    }
  };

  const popupRef = useRef<HTMLDivElement | null>(null);
  const enteredRef = useRef<boolean>(false);

  const mouseMoveEvent = useCallback((event: MouseEvent) => {
   
    const sideMenu = document.getElementById("side-menu-wrapper");
  
    if (sideMenu && !enteredRef.current) {
      const sideMenuWidth = sideMenu.getBoundingClientRect();
      const sideMenuLeft = sideMenuWidth.left;

      if (sideMenuLeft < event.clientX) {
        enteredRef.current = true;
        if (props.id && props.addItem) {
          props.addItem(props.id, "rain");
          /* props.windowState(false) */
          setShowDialog(false);
        }
      }
    }
  }, []);

  const touchMoveEvent = useCallback((event: TouchEvent) => {
  const sideMenu = document.getElementById('side-menu-wrapper');
  
  if (sideMenu && !enteredRef.current) {
    const sideMenuWidth = sideMenu.getBoundingClientRect()
    const sideMenuLeft = sideMenuWidth.left

    const touchX = event.touches[0].clientX; 

    if (sideMenuLeft < touchX) {
        enteredRef.current = true;
        if (props.id && props.addItem) {
          props.addItem(props.id, "rain");
          /* props.windowState(false) */
          setShowDialog(false);
        }
      }
    }
  }, []);

 useEffect(() => {
    const handleMouseDown = () => {
      document.addEventListener("mousemove", mouseMoveEvent);
      document.addEventListener("mouseup", handleMouseUp);
    };

    const handleTouchStart = () => {
      document.addEventListener("touchmove", touchMoveEvent);
      document.addEventListener("touchend", handleTouchEnd);
    };

    const handleMouseUp = () => {
      document.removeEventListener("mousemove", mouseMoveEvent);
    };

    const handleTouchEnd = () => {
      document.removeEventListener("touchmove", touchMoveEvent);
    };

    if (popupRef.current) {
      popupRef.current.addEventListener("mouseenter", () => {
        popupRef.current?.addEventListener("mousedown", handleMouseDown);
        popupRef.current?.addEventListener("touchstart", handleTouchStart);
      });
    }

    return () => {
      document.removeEventListener("mousemove", mouseMoveEvent);
      document.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener("touchmove", touchMoveEvent);
      document.removeEventListener("touchend", handleTouchEnd);
    };
  }, [mouseMoveEvent, touchMoveEvent]);

  return (
    <div
      className="ol-overlay-container ol-selectable"
      style={{
        width: "10px",
        height: "10px",
      }}
    >
      <div
        style={{ visibility: showDialog ? "visible" : "hidden" }}
        ref={parentContainerRef}
      >
        <Draggable
          handle=".drag-handle"
          cancel=".no-drag"
          enableUserSelectHack={false}
        >
          <ResizableBox
            className="custom-resizable-box"
            width={dimensions.resizableBoxwidth}
            height={dimensions.resizableBoxheight}
            minConstraints={[
              dimensionsMin.resizableBoxMinwidth,
              dimensionsMin.resizableBoxMinheight,
            ]}
            onResize={handleResize}
            /* maxConstraints={[800, 600]} */ resizeHandles={[
              "se",
              "sw",
              "ne",
              "nw",
            ]}
            style={{ zIndex: 1, left: "-30vw", right: "0vw", top: "-40vh" }}
          >
            <div
              className="drag-handle"
              ref={fullscreenModal}
              style={{
                bottom: 0,
                margin: "auto",
                width: "100%", //"65vw",
                height: "100%", //"60vh,"
                position: "absolute",
                cursor: "move",
                touchAction: "none",
              }}
            >
              <div className={`rainfall-popup`}>
                {/* Title container */}
                <div className={`titleContainer`} ref={popupRef}>
                  <span>{props.name}</span>
                  <div className={`titleButtonContainer`}>
                    <i
                      className={`titleIcon fullScreenIco`}
                      onClick={openContentFullscreen}
                      onTouchStart={openContentFullscreen}
                    ></i>
                    <i
                      className={`titleIcon closeIco`}
                      onClick={() => {
                        hideDialog(props.sensorId);
                      }}
                      onTouchEnd={() => {
                        hideDialog(props.sensorId);
                      }}
                    ></i>
                  </div>
                </div>

                {/* Container */}
                <div className={`rainfall-container`}>
                  <div className={`chart-container`}>
                    <div className={`chart1-wrapper`}>
                      {activeFullScreen && (
                        <div
                          className="no-drag"
                          style={{
                            height: "40vh",
                            width: "95%",
                            display: "flex",
                            alignItems: "start",
                            margin: "0px",
                            flexDirection: "column",
                            justifyContent: "top",
                          }}
                        >
                          <RainLastDayChart
                            id={props.sensorId}
                            fullScreen={activeFullScreen}
                            showDialog={showDialog}
                            chartWidth={widthMain * 0.92}
                            chartHeight={heightMain * 0.4}
                            location={props.name}
                            currentDay={"Current Rainfall"}
                            differant={"Differance"}
                          />
                        </div>
                      )}
                      {!activeFullScreen && (
                        <div
                          className="no-drag"
                          style={{
                            height: "38vh",
                            width: "100%",
                            display: "flex",
                            alignItems: "start",
                            margin: "0px",
                            flexDirection: "column",
                            justifyContent: "top",
                          }}
                        >
                          <RainLastDayChart
                            id={props.sensorId}
                            fullScreen={activeFullScreen}
                            showDialog={showDialog}
                            chartWidth={widthMain * 0.92}
                            chartHeight={heightMain * 0.6}
                            location={props.name}
                            currentDay={"Current Rainfall"}
                            differant={"Differance"}
                          />
                        </div>
                      )}
                    </div>
                    <div className={`chart2-wrapper`}>
                      {activeFullScreen && (
                        <div
                          className="no-drag"
                          style={{
                            height: "20vh",
                            width: "95%",
                            display: "flex",
                            alignItems: "start",
                            margin: "0px",
                            flexDirection: "column",
                            justifyContent: "top",
                          }}
                        >
                          <RainCurrentChart
                            id={props.sensorId}
                            fullScreen={activeFullScreen}
                            showDialog={showDialog}
                            chartWidth={widthMain * 0.92}
                            chartHeight={heightMain * 0.4}
                            location={props.name}
                          />
                        </div>
                      )}
                      {!activeFullScreen && (
                        <div
                          className="no-drag"
                          style={{
                            height: "100%",
                            width: "100%",
                            display: "flex",
                            alignItems: "start",
                            margin: "0px",
                            flexDirection: "column",
                            justifyContent: "top",
                          }}
                        >
                          <RainCurrentChart
                            id={props.sensorId}
                            fullScreen={activeFullScreen}
                            showDialog={showDialog}
                            chartWidth={widthMain * 0.92}
                            chartHeight={heightMain * 0.6}
                            location={props.name}
                          />
                        </div>
                      )}
                    </div>
                    <div className={`chart3-wrapper`}>
                      {activeFullScreen && (
                        <div
                          className="no-drag"
                          style={{
                            height: "20vh",
                            width: "95%",
                            display: "flex",
                            alignItems: "start",
                            margin: "0px",
                            flexDirection: "column",
                            justifyContent: "top",
                          }}
                        >
                          <RainDiffChart
                            id={props.sensorId}
                            fullScreen={activeFullScreen}
                            showDialog={showDialog}
                            chartWidth={widthMain * 0.92}
                            chartHeight={heightMain * 0.4}
                            location={props.name}
                          />
                        </div>
                      )}
                      {!activeFullScreen && (
                        <div
                          className="no-drag"
                          style={{
                            height: "100%",
                            width: "100%",
                            display: "flex",
                            alignItems: "start",
                            margin: "0px",
                            flexDirection: "column",
                            justifyContent: "top",
                          }}
                        >
                          <RainDiffChart
                            id={props.sensorId}
                            fullScreen={activeFullScreen}
                            showDialog={showDialog}
                            chartWidth={widthMain * 0.92}
                            chartHeight={heightMain * 0.6}
                            location={props.name}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                  <div className={`data-bar`}>
                    <div className={`detailItem`}>
                      <span className={`label`}>Current: </span>
                      <span className={`data`}>{current} mm</span>
                    </div>
                    <div className={`detailItem`}>
                      <span className={`label`}>Diff: </span>
                      <span className={`data`}>{diff} mm</span>
                    </div>
                    <div className={`detailItem`}>
                      <span className={`label`}>24h: </span>
                      <span className={`data`}>{last24} mm</span>
                    </div>
                  </div>
                  <div className={`data2-bar`}>
                    <div className={`detailItem`}>
                      <span className={`label`}>Last data: </span>
                      <span className={`data`}>{lastUpdate}</span>
                    </div>
                    <div className={`detailItem`}>
                      <span className={`label`}>Last update on: </span>
                      <span className={`data`}>{lastUpdate}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </ResizableBox>
        </Draggable>
      </div>
    </div>
  );
};

export default RainfallPopup;
